import React, {createContext, useCallback, useContext, useEffect, useMemo, useState} from "react";
import {SessionContext} from "./SessionContext";
import {useAssetGroupState} from "./AssetGroupContext";
import useCurrentOrg from "../components/helpers/useCurrentOrg";
import {isMobile} from "../plugins/device-detect";
import {buildViewSettingsDefaults, resolveViewSettings, sessionViewSettingsForTheme} from "../components/explore/viewSettings";
import {useThemeMode} from "./ThemeContext";

const ViewSettingsContext = createContext(null);

const ViewSettingsProvider = ({children})=>{
    const {state, dispatch} = useContext(SessionContext);

    const currentOrg = useCurrentOrg()
    const {currentAssetGroup} = useAssetGroupState()
    const {isDark, resolvedTheme} = useThemeMode()

    const defaults = buildViewSettingsDefaults({
        mobile: isMobile(),
        dark: isDark,
        organizationSettings: currentOrg?.view_settings,
    })

    const [manual, setManual] = useState()
    // Re-apply the asset group's saved settings on a new group and when the theme flips, so a
    // group's own grid color isn't shadowed by a stale manual flag from the other theme.
    useEffect(()=>{
        setManual(false)
    }, [currentAssetGroup?.id, resolvedTheme])

    // console.log('defaults: ', defaults, 'manual:', manual, 'view_settings:',  currentAssetGroup?.view_settings)

    const sessionSettings = sessionViewSettingsForTheme(state, resolvedTheme)

    const {viewMode, masonrySizing, gridSize, gridColor, tocHidden, showFilenames, autoPlay, showIcons, showRating, showHeadline, convertCommasToOr, convertSpacesToOr, colorLabel} =
        resolveViewSettings({
            defaults,
            sessionSettings,
            assetGroupSettings: currentAssetGroup?.view_settings,
            manual,
        });

    // True when the grid color came from a real choice (viewer, asset group, or org) rather than the
    // theme default. ViewMenu uses this so saving other view settings doesn't persist a theme color.
    const gridColorExplicit = !!(
        sessionSettings.gridColor ||
        (!manual && currentAssetGroup?.view_settings?.gridColor) ||
        currentOrg?.view_settings?.gridColor
    )

    const changeViewMode = useCallback((e)=>{
        const val = typeof e == 'string' ? e : e.target.value
        if(val != viewMode) dispatch({type:'setViewMode', viewMode: val})
        setManual(true)
    }, [viewMode, dispatch])

    const changeGridSize = useCallback((e)=>{
        const val = typeof e == 'string' ? e : e.target.value
        if(val != gridSize) dispatch({type:'setGridSize', gridSize: val})
        setManual(true)
    }, [gridSize, dispatch])

    const changeMasonrySizing = useCallback(e => {
        const val = typeof e === 'string' ? e : e.target.value
        if(val !== masonrySizing || !manual) dispatch({type: 'setMasonrySizing', masonrySizing: val,
            // Entering manual mode removes the group overlay. Preserve what the
            // viewer currently sees so changing sizing does not also change modes.
            ...(!manual && {viewSettings: {viewMode, gridSize, showHeadline, showRating, showFilenames,
                showIcons, autoPlay, colorLabel, gridColor: gridColorExplicit ? gridColor : undefined}, theme: resolvedTheme}),
        })
        setManual(true)
    }, [masonrySizing, dispatch, manual, viewMode, gridSize, showHeadline, showRating, showFilenames,
        showIcons, autoPlay, colorLabel, gridColor, gridColorExplicit, resolvedTheme])

    const changeGridColor = useCallback(color =>{
        const val = typeof color == 'string' ? color : color.hex
        if(val != gridColor) dispatch({type: 'setGridColor', gridColor: val, theme: resolvedTheme})
        setManual(true)
    }, [gridColor, dispatch, resolvedTheme])

    const changeShowFilenames = useCallback(e =>{
        const val = e.target.checked
        if(val != showFilenames) dispatch({type: 'setShowFilenames', showFilenames: val})
        setManual(true)
    }, [showFilenames, dispatch])

    const changeShowHeadline = useCallback(e =>{
        const val = e.target.checked
        if(val !== showHeadline) dispatch({type: 'setShowHeadline', showHeadline: val})
        setManual(true)
    }, [showHeadline, dispatch])

    const changeAutoPlay = useCallback(e =>{
        const val = e.target.checked
        if(val != autoPlay) dispatch({type: 'setAutoPlay', autoPlay: val})
        setManual(true)
    }, [autoPlay, dispatch])

    const changeShowIcons = useCallback(e =>{
        const val = e.target.checked
        if(val != showIcons) dispatch({type: 'setShowIcons', showIcons: val})
        setManual(true)
    }, [showIcons, dispatch])

    const changeShowRating = useCallback(e =>{
        const val = e.target.checked
        if(val !== showRating) dispatch({type: 'setShowRating', showRating: val})
        setManual(true)
    }, [showRating, dispatch])

    const changeColorLabel = useCallback(val =>{
        dispatch({type: 'setColorLabel', colorLabel: val})
        setManual(true)
    }, [dispatch])

    const toggleToc = useCallback(()=>{
        dispatch({type:'setTocHidden', tocHidden: !tocHidden})
    }, [tocHidden, dispatch])

    const changeConvertCommasToOr = useCallback(e => {
        const val = e.target.checked
        if(val != convertCommasToOr) dispatch({type:'setConvertCommasToOr', convertCommasToOr: val})
        setManual(true)
    }, [convertCommasToOr, dispatch])

    const changeConvertSpacesToOr = useCallback(e => {
        const val = e.target.checked
        if(val != convertSpacesToOr) dispatch({type:'setConvertSpacesToOr', convertSpacesToOr: val})
        setManual(true)
    }, [convertSpacesToOr, dispatch])

    const contextValue = useMemo(() => ({
        defaults, viewMode, masonrySizing, changeMasonrySizing, gridSize, gridColor, gridColorExplicit, tocHidden,
        changeGridSize, changeGridColor, changeViewMode, toggleToc,
        showFilenames, changeShowFilenames, autoPlay, changeAutoPlay, showIcons, changeShowIcons,
        showRating, changeShowRating,
        showHeadline, changeShowHeadline,
        convertCommasToOr,
        changeConvertCommasToOr,
        changeConvertSpacesToOr,
        convertSpacesToOr,
        colorLabel, changeColorLabel,
    }), [
        defaults, viewMode, masonrySizing, changeMasonrySizing, gridSize, gridColor, gridColorExplicit, tocHidden,
        changeGridSize, changeGridColor, changeViewMode, toggleToc,
        showFilenames, changeShowFilenames, autoPlay, changeAutoPlay, showIcons, changeShowIcons,
        showRating, changeShowRating,
        showHeadline, changeShowHeadline,
        convertCommasToOr, changeConvertCommasToOr,
        changeConvertSpacesToOr, convertSpacesToOr,
        colorLabel, changeColorLabel,
    ])

    return (
        <ViewSettingsContext.Provider value={contextValue}>
            {children}
        </ViewSettingsContext.Provider>
    )
}

export {ViewSettingsContext, ViewSettingsProvider}
